import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import { ALTURA_PLOT, AreaPlot, Grafico, MARGEM_TOPO, Rolagem } from './styles';
import { ALTURA_SVG, EixoY, Grade, PainelSelecao, RotuloX, criarEscalaY } from './Eixo';
import { passoRotulos, ticksAgradaveis } from './escala';
import Legenda from './Legenda';
import useLargura from './useLargura';
import { Formato, Serie } from '../../types/Dashboard';

// Barra ancorada na linha de base, com a ponta de dados arredondada (4px)
function caminhoBarra(x: number, w: number, yBase: number, yValor: number) {
  const r = Math.min(4, w / 2, Math.abs(yBase - yValor));
  if (yValor <= yBase) {
    return `M ${x} ${yBase} L ${x} ${yValor + r} Q ${x} ${yValor} ${x + r} ${yValor} L ${x + w - r} ${yValor} Q ${x + w} ${yValor} ${x + w} ${yValor + r} L ${x + w} ${yBase} Z`;
  }
  return `M ${x} ${yBase} L ${x} ${yValor - r} Q ${x} ${yValor} ${x + r} ${yValor} L ${x + w - r} ${yValor} Q ${x + w} ${yValor} ${x + w} ${yValor - r} L ${x + w} ${yBase} Z`;
}

// Valores ao longo do tempo (uma ou mais séries agrupadas). Muitos pontos: rola na horizontal.
export default function BarrasVerticais({ series, formato }: { series: Serie[]; formato: Formato }) {
  const theme = useTheme();
  const { ref, largura } = useLargura<HTMLDivElement>();
  const [sel, setSel] = useState<number | null>(null);

  const qtd = Math.max(...series.map(s => s.pontos.length));
  const valores = series.flatMap(s => s.pontos.map(p => p.valor));
  const ticks = ticksAgradaveis(Math.min(...valores), Math.max(...valores));
  const y = criarEscalaY(ticks);
  const yZero = y(0);

  const larguraBarra = series.length > 1 ? 8 : 12;
  const minGrupo = series.length * larguraBarra + (series.length - 1) * 2 + 8;
  const grupo = qtd > 0 ? Math.max(minGrupo, largura / qtd) : minGrupo;
  const larguraTotal = Math.max(largura, grupo * qtd);
  const passo = passoRotulos(qtd, grupo);
  // 2px de espaço entre as barras do mesmo grupo
  const larguraBarraReal = Math.min(larguraBarra * 2, (grupo - 8 - (series.length - 1) * 2) / series.length);

  return (
    <Grafico onMouseLeave={() => setSel(null)}>
      <PainelSelecao series={series} indice={sel} formato={formato} />
      <AreaPlot>
        <EixoY ticks={ticks} y={y} formato={formato} />
        <Rolagem ref={ref}>
          {largura > 0 && (
            <svg width={larguraTotal} height={ALTURA_SVG} role='img' aria-label={series.map(s => s.nome).join(', ')}>
              <Grade ticks={ticks} y={y} largura={larguraTotal} />
              {Array.from({ length: qtd }, (_, i) => {
                const x0 = i * grupo;
                const larguraBarras = series.length * larguraBarraReal + (series.length - 1) * 2;
                const inicio = x0 + (grupo - larguraBarras) / 2;
                return (
                  <g key={i}>
                    {sel === i && (
                      <rect x={x0} y={MARGEM_TOPO} width={grupo} height={ALTURA_PLOT} fill={theme.cores.selecionado} />
                    )}
                    {series.map((s, si) => {
                      const v = s.pontos[i]?.valor ?? 0;
                      if (v === 0) return null;
                      return (
                        <path
                          key={s.nome}
                          d={caminhoBarra(inicio + si * (larguraBarraReal + 2), larguraBarraReal, yZero, y(v))}
                          fill={theme.cores.serie[si]}
                        />
                      );
                    })}
                    {i % passo === 0 && <RotuloX x={x0 + grupo / 2} texto={series[0]?.pontos[i]?.rotulo ?? ''} largura={larguraTotal} />}
                    {/* área de hover/toque do grupo inteiro, maior que a barra */}
                    <rect
                      x={x0}
                      y={0}
                      width={grupo}
                      height={ALTURA_SVG}
                      fill='transparent'
                      onMouseEnter={() => setSel(i)}
                      onClick={() => setSel(i)}
                    />
                  </g>
                );
              })}
            </svg>
          )}
        </Rolagem>
      </AreaPlot>
      <Legenda nomes={series.map(s => s.nome)} />
    </Grafico>
  );
}
